import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Send, Volume2, VolumeX, FileText, Loader2, Bot, User, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { LANGS, t } from '../i18n';
import { createRecognizer, speak, stopSpeaking, supportsSTT } from '../lib/speech';
import { isUserLoggedIn } from '../lib/auth';

export default function Chat({ lang, setLang }) {
  const nav = useNavigate();
  const [stage, setStage] = useState('onboarding'); // onboarding | chat
  const [onboardingStep, setOnboardingStep] = useState('choose_mode');
  const [onboardingMessages, setOnboardingMessages] = useState([
    { role: 'assistant', content: 'Hi, I am Health Buddy. What would you like to do today?' },
  ]);
  const [patient, setPatient] = useState({ name: '', age: '', gender: 'male', phone: '', medical_history: '', purpose: 'symptoms', filling_for: 'self' });
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const recRef = useRef(null);
  const sendingRef = useRef(false);
  const bottomRef = useRef(null);
  const speechLang = LANGS.find(l=>l.code===lang)?.speech || 'en-IN';
  const latestAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content || '';
  const recapReady = latestAssistant.toLowerCase().includes('please review this quick recap');
  const userTurns = messages.filter((m) => m.role === 'user').length;
  const reportQuality = Math.min(100, Math.max(25, (userTurns * 18) + (recapReady ? 28 : 0)));
  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];
  const latestAssistantOptions = (() => {
    const options = latestAssistant
      .split('\n')
      .map((line) => line.match(/^\s*\d+\.\s+(.+?)\s*$/))
      .filter(Boolean)
      .map((match) => match[1]);
    return options.length >= 2 ? options : [];
  })();
  const smartReplyOptions = (() => {
    const low = latestAssistant.toLowerCase();
    if (latestAssistantOptions.length) return latestAssistantOptions;
    if (low.includes('full') && low.includes('short')) return ['Short', 'Full'];
    if (low.includes('gender') || (low.includes('male') && low.includes('female'))) return ['Male', 'Female', 'Other'];
    if (low.includes('bp') || low.includes('blood sugar')) return ['Not known', 'BP normal', 'Sugar normal'];
    if (low.includes('smoke') || low.includes('alcohol') || low.includes('tobacco')) return ['No smoking/alcohol', 'Smoking', 'Alcohol'];
    if (low.includes('do you') || low.includes('are you') || low.includes('any ')) return ['Yes', 'No', 'Not known'];
    return [];
  })();
  const showNoneSmartReply = latestAssistantOptions.length > 0;
  const relationLabels = {
    self: 'Myself',
    mother: 'Mother',
    father: 'Father',
    child: 'Child',
    spouse: 'Spouse',
    other: 'Someone else',
  };

  useEffect(() => {
    if (!isUserLoggedIn()) nav('/login');
  }, [nav]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    (async () => {
      try {
        const { data } = await api.get('/sessions/me/active');
        if (data?.session?.id) {
          toast.success(t(lang, 'loading_active_session'));
          setSessionId(data.session.id);
          setMessages(data.session.messages || []);
          setPatient((p) => ({ ...p, ...(data.session.patient || {}) }));
          setStage('chat');
          localStorage.setItem('hb_active_session', data.session.id);
        } else {
          localStorage.removeItem('hb_active_session');
        }
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { setSelectedOptions([]); }, [latestAssistant]);


  const startSession = async (patientOverride = patient) => {
    const draft = patientOverride;
    if (!draft.name || !draft.age || !draft.phone) {
      toast.error(t(lang, 'fill_name_age_phone')); return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/session/start', {
        patient: { ...draft, age: parseInt(draft.age), language: lang },
      });
      setSessionId(data.session_id);
      localStorage.setItem('hb_active_session', data.session_id);
      setMessages(data.messages);
      setStage('chat');
      if (ttsOn) speak(data.reply, speechLang);
    } catch (e) {
      toast.error(t(lang, 'could_not_start_session'));
    } finally { setSending(false); }
  };

  const addOnboardingMessage = (message) => {
    setOnboardingMessages((items) => [...items, message]);
  };

  const promptNext = (content, nextStep) => {
    addOnboardingMessage({ role: 'assistant', content });
    setOnboardingStep(nextStep);
  };

  const handleOnboardingSubmit = async (value) => {
    const msg = (value ?? input).trim();
    if (!msg || sendingRef.current) return;
    sendingRef.current = true;
    setInput('');
    addOnboardingMessage({ role: 'user', content: msg });

    const normalized = msg.toLowerCase();
    const nextPatient = { ...patient };

    try {
      if (onboardingStep === 'choose_mode') {
        const isCheckup = normalized.includes('checkup') || normalized.includes('general') || normalized === '2';
        nextPatient.purpose = isCheckup ? 'checkup' : 'symptoms';
        setPatient(nextPatient);
        promptNext(
          isCheckup
            ? 'Great, we will do a general health checkup. What is your full name?'
            : 'Okay, we will check your illness or symptoms. What is your full name?',
          'name'
        );
        return;
      }

      if (onboardingStep === 'name') {
        if (msg.length < 2) {
          promptNext('Please enter your name with at least 2 letters.', 'name');
          return;
        }
        nextPatient.name = msg;
        setPatient(nextPatient);
        promptNext(`Thanks, ${msg}. What is your age?`, 'age');
        return;
      }

      if (onboardingStep === 'age') {
        const age = parseInt(msg.replace(/\D/g, ''), 10);
        if (!age || age < 1 || age > 120) {
          promptNext('Please enter a valid age between 1 and 120.', 'age');
          return;
        }
        nextPatient.age = String(age);
        setPatient(nextPatient);
        promptNext('Please choose your gender.', 'gender');
        return;
      }

      if (onboardingStep === 'gender') {
        const gender = normalized.includes('female') ? 'female' : normalized.includes('other') ? 'other' : 'male';
        nextPatient.gender = gender;
        setPatient(nextPatient);
        promptNext('What phone number should we attach to this health record?', 'phone');
        return;
      }

      if (onboardingStep === 'phone') {
        const cleanedPhone = msg.replace(/[^\d+]/g, '');
        if (!/^\+?[0-9]{10,15}$/.test(cleanedPhone)) {
          promptNext('Please enter a valid phone number, for example +919876543210.', 'phone');
          return;
        }
        nextPatient.phone = cleanedPhone;
        setPatient(nextPatient);
        promptNext('Who is this consultation for?', 'filling_for');
        return;
      }

      if (onboardingStep === 'filling_for') {
        const relation = Object.keys(relationLabels).find((key) => normalized.includes(key)) || 'self';
        nextPatient.filling_for = relation;
        setPatient(nextPatient);
        promptNext('Any medical history, allergies, medicines, or past conditions I should know? Type "skip" if none.', 'history');
        return;
      }

      if (onboardingStep === 'history') {
        nextPatient.medical_history = ['skip', 'none', 'no'].includes(normalized) ? '' : msg;
        setPatient(nextPatient);
        addOnboardingMessage({
          role: 'assistant',
          content: nextPatient.purpose === 'checkup'
            ? 'Perfect. Starting your general checkup now.'
            : 'Perfect. Starting your symptom check now.',
        });
        await startSession(nextPatient);
      }
    } finally {
      sendingRef.current = false;
    }
  };

  const onboardingOptions = () => {
    if (onboardingStep === 'choose_mode') {
      return [
        { label: 'Illness / symptoms', value: 'Illness / symptoms' },
        { label: 'General checkup', value: 'General checkup' },
      ];
    }
    if (onboardingStep === 'gender') {
      return ['Male', 'Female', 'Other'].map((value) => ({ label: value, value }));
    }
    if (onboardingStep === 'filling_for') {
      return Object.entries(relationLabels).map(([value, label]) => ({ label, value: label }));
    }
    if (onboardingStep === 'history') {
      return [{ label: 'Skip', value: 'skip' }];
    }
    return [];
  };

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || !sessionId || sendingRef.current) return;
    sendingRef.current = true;
    setInput('');
    const pendingId = `pending-${Date.now()}`;
    const now = new Date().toISOString();
    const waitText = lang === 'hi'
      ? 'मैंने आपका संदेश ले लिया है। जवाब तैयार कर रहा हूँ...'
      : lang === 'te'
        ? 'మీ మెసేజ్ అందింది. సమాధానం సిద్ధం చేస్తున్నాను...'
        : lang === 'ta'
          ? 'உங்கள் செய்தி கிடைத்தது. பதில் தயார் செய்கிறேன்...'
          : 'I got your message. Preparing the next question...';
    setMessages((m) => [
      ...m,
      { role: 'user', content: msg, ts: now },
      { id: pendingId, role: 'assistant', content: waitText, ts: now, pending: true },
    ]);

    setSending(true);
    try {
      const { data } = await api.post('/chat', { session_id: sessionId, message: msg });
      setMessages((m) => m.map((item) =>
        item.id === pendingId
          ? { role: 'assistant', content: data.reply, ts: new Date().toISOString() }
          : item
      ));
      if (ttsOn) speak(data.reply, speechLang);
      
      if (data.status === 'completed') {
        toast.success(patient.purpose === 'checkup'
          ? 'Checkup recap ready. Review it before saving the report.'
          : 'Consultation recap ready. Review it before preparing the report.');
      } else {
        const lowSymptomPhrases = ['no illness', "i'm good", 'i am good', 'no', 'none'];
        const normalized = msg.toLowerCase();
        if (lowSymptomPhrases.some((p) => normalized.includes(p)) && sessionId) {
          try {
            await api.post(`/session/${sessionId}/save-profile`);
            toast.success('Health profile saved');
          } catch (e) {
            // non-fatal
          }
        }
      }
    } catch (e) {
      setMessages((m) => m.filter((item) => item.id !== pendingId));
      toast.error(t(lang, 'buddy_could_not_reply'));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const toggleSelectedOption = (option) => {
    setSelectedOptions((items) =>
      items.includes(option) ? items.filter((item) => item !== option) : [...items, option]
    );
  };

  const sendSelectedOptions = () => {
    if (!smartReplyOptions.length) return;
    const answer = selectedOptions.length ? selectedOptions.join(', ') : 'none';
    sendMessage(answer);
  };

  const toggleListen = () => {
    if (!supportsSTT()) { toast.error(t(lang, 'voice_not_supported')); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const r = createRecognizer(speechLang);
    if (!r) return;
    recRef.current = r;
    let finalText = '';
    r.onresult = (e) => { finalText = e.results[0][0].transcript; };
    r.onend = () => {
      setListening(false);
      if (finalText) {
        setInput(finalText);
        toast.success('Voice captured. Review and tap send.');
      }
    };
    r.onerror = () => setListening(false);
    r.start();
    setListening(true);
  };

  const finishAndReport = async () => {
    if (!sessionId) return;
    setGeneratingReport(true);
    stopSpeaking();
    try {
      const { data } = await api.post(`/report/generate?session_id=${sessionId}`);
      localStorage.removeItem('hb_active_session');
      nav(`/report/${data.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to generate report';
      toast.error(msg);
    } finally { setGeneratingReport(false); }
  };

  // ------------ RENDER -------------
  if (stage === 'onboarding') {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Card className="overflow-hidden rounded-2xl sm:rounded-3xl border-2 border-[#E8E1D5] bg-white hb-shadow-lg">
          <div className="flex items-center justify-between px-5 py-4 bg-[#1E3F33] text-white">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-[#E07A5F] grid place-items-center"><Bot className="h-6 w-6"/></div>
              <div>
                <div className="font-display text-xl leading-none">Health Buddy</div>
                <div className="text-xs text-white/70 mt-1">Guided intake</div>
              </div>
            </div>
          </div>

          <div className="h-[58vh] min-h-[420px] overflow-y-auto bg-[#FAFAFA] px-3 sm:px-4 py-5 space-y-3">
            {onboardingMessages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 msg-in ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'assistant' && (
                  <div className="h-8 w-8 rounded-full bg-[#1E3F33] grid place-items-center shrink-0"><Bot className="h-4 w-4 text-white"/></div>
                )}
                <div className={`${m.role === 'user'
                  ? 'bg-[#1E3F33] text-white rounded-3xl rounded-tr-md'
                  : 'bg-[#F4F1EA] text-[#1A1A1A] rounded-3xl rounded-tl-md'} px-4 sm:px-5 py-3 sm:py-3.5 text-[15px] sm:text-[16px] leading-relaxed max-w-[86%] sm:max-w-[80%] shadow-sm whitespace-pre-wrap break-words`}>
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-[#E07A5F] grid place-items-center shrink-0"><User className="h-4 w-4 text-white"/></div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-[#7A7A7A] text-sm pl-10">
                <Loader2 className="h-4 w-4 animate-spin" /> Starting your chat...
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          <div className="border-t border-[#E8E1D5] p-4 bg-white">
            {onboardingOptions().length ? (
              <div className="mb-3 flex flex-wrap gap-2">
                {onboardingOptions().map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant="outline"
                    onClick={() => handleOnboardingSubmit(option.value)}
                    disabled={sending || sendingRef.current}
                    className="rounded-full border-[#1E3F33] text-[#1E3F33] font-semibold"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            ) : null}
            <div className="flex items-center gap-2 sm:gap-3">
              <Input
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if(e.key === 'Enter'){ e.preventDefault(); handleOnboardingSubmit(); }}}
                placeholder="Type your answer"
                className="h-12 sm:h-14 min-w-0 rounded-full border-[#E2E8F0] text-base px-4 sm:px-5"
              />
              <Button onClick={()=>handleOnboardingSubmit()} disabled={sending || !input.trim()}
                className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] p-0">
                <Send className="h-5 w-5"/>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Card className="overflow-hidden rounded-2xl sm:rounded-3xl border-2 border-[#E8E1D5] bg-white hb-shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[#1E3F33] text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[#E07A5F] grid place-items-center"><Bot className="h-6 w-6"/></div>
            <div>
              <div className="font-display text-xl leading-none">Health Buddy</div>
              <div className="text-xs text-white/70 mt-1 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-400"/>Online • {currentLang.label}</div>
            </div>
          </div>
          <div className="flex gap-1.5">
            <Button size="icon" variant="ghost" onClick={()=>{ setTtsOn(v=>!v); stopSpeaking(); }} className="rounded-full text-white hover:bg-white/10" data-testid="toggle-tts-btn">
              {ttsOn ? <Volume2 className="h-5 w-5"/> : <VolumeX className="h-5 w-5"/>}
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="h-[58vh] min-h-[360px] sm:min-h-[420px] overflow-y-auto bg-[#FAFAFA] px-3 sm:px-4 py-5 space-y-3" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-end gap-2 msg-in ${m.role==='user' ? 'justify-end' : ''}`}>
              {m.role==='assistant' && (
                <div className="h-8 w-8 rounded-full bg-[#1E3F33] grid place-items-center shrink-0"><Bot className="h-4 w-4 text-white"/></div>
              )}
              <div className={`${m.role==='user'
                ? 'bg-[#1E3F33] text-white rounded-3xl rounded-tr-md'
                : 'bg-[#F4F1EA] text-[#1A1A1A] rounded-3xl rounded-tl-md'} px-4 sm:px-5 py-3 sm:py-3.5 text-[15px] sm:text-[16px] leading-relaxed max-w-[86%] sm:max-w-[80%] shadow-sm whitespace-pre-wrap break-words`}>
                {m.content}
              </div>
              {m.role==='user' && (
                <div className="h-8 w-8 rounded-full bg-[#E07A5F] grid place-items-center shrink-0"><User className="h-4 w-4 text-white"/></div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-[#7A7A7A] text-sm pl-10">
              <div className="flex gap-1"><span className="h-2 w-2 bg-[#1E3F33] rounded-full animate-bounce"/><span className="h-2 w-2 bg-[#1E3F33] rounded-full animate-bounce [animation-delay:150ms]"/><span className="h-2 w-2 bg-[#1E3F33] rounded-full animate-bounce [animation-delay:300ms]"/></div>
              Buddy is thinking…
            </div>
          )}
          <div ref={bottomRef}/>
        </div>

        {/* Input bar */}
        <div className="border-t border-[#E8E1D5] p-4 bg-white">
          <div className="mb-3 rounded-2xl border border-[#D8E9DF] bg-[#F6FBF8] px-4 py-3">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[#1E3F33] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1E3F33]">Guided doctor-ready intake</div>
                <div className="text-xs text-[#5A5A5A] mt-1">
                  Buddy asks only relevant questions, then shows a recap before report generation.
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-2 flex-1 rounded-full bg-white overflow-hidden border border-[#D8E9DF]">
                    <div className="h-full rounded-full bg-[#1E3F33]" style={{ width: `${reportQuality}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-[#1E3F33] whitespace-nowrap">{reportQuality}% ready</span>
                </div>
              </div>
            </div>
          </div>
          {recapReady && (
            <div className="mb-3 rounded-2xl border border-[#E8E1D5] bg-[#FFFDF8] p-3">
              <div className="text-sm font-semibold text-[#1E3F33]">Review recap before sending</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button onClick={finishAndReport} disabled={generatingReport} className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
                  Looks correct
                </Button>
                <Button variant="outline" onClick={() => setInput('Correction: my main symptom is ')} className="rounded-full border-[#D8DDE5]">
                  Edit symptom
                </Button>
                <Button variant="outline" onClick={() => setInput('Correction: it started ')} className="rounded-full border-[#D8DDE5]">
                  Edit duration
                </Button>
                <Button variant="outline" onClick={() => setInput('Correction: severity is ')} className="rounded-full border-[#D8DDE5]">
                  Edit severity
                </Button>
              </div>
            </div>
          )}
          {smartReplyOptions.length ? (
            <div className="mb-3 rounded-2xl border border-[#D8E9DF] bg-[#F6FBF8] p-3">
              <div className="text-sm font-semibold text-[#1E3F33]">{latestAssistantOptions.length ? 'Select all that apply' : 'Choose one'}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {smartReplyOptions.map((option) => {
                  const active = selectedOptions.includes(option);
                  return (
                    <Button
                      key={option}
                      type="button"
                      variant={active ? 'default' : 'outline'}
                      onClick={() => {
                        if (latestAssistantOptions.length) {
                          toggleSelectedOption(option);
                        } else {
                          sendMessage(option);
                        }
                      }}
                      disabled={sending}
                      className={active
                        ? 'rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white'
                        : 'rounded-full border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#F4F1EA]'}
                    >
                      {option}
                    </Button>
                  );
                })}
              </div>
              {latestAssistantOptions.length ? (
              <div className="mt-3 flex gap-2">
                {showNoneSmartReply ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setSelectedOptions([]); sendMessage('none'); }}
                    disabled={sending}
                    className="rounded-full border-[#D8DDE5] text-[#4A4A4A]"
                  >
                    None
                  </Button>
                ) : null}
                <Button
                  type="button"
                  onClick={sendSelectedOptions}
                  disabled={sending}
                  className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white"
                >
                  Continue
                </Button>
              </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleListen}
              className={`h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full grid place-items-center text-white transition-transform ${listening ? 'bg-[#D9534F] voice-pulse' : 'bg-[#E07A5F] hover:scale-105'}`}
              aria-label="voice" data-testid="voice-mic-btn">
              {listening ? <MicOff className="h-6 w-6"/> : <Mic className="h-6 w-6"/>}
            </button>
            <Input
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              onKeyDown={(e)=>{ if(e.key==='Enter' && !sendingRef.current){ e.preventDefault(); sendMessage(); }}}
              placeholder={t(lang,'type_here')}
              className="h-12 sm:h-14 min-w-0 rounded-full border-[#E2E8F0] text-base px-4 sm:px-5"
              data-testid="chat-text-input"
            />
            <Button onClick={()=>sendMessage()} disabled={sending || !input.trim()}
              className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] p-0"
              data-testid="chat-send-btn">
              <Send className="h-5 w-5"/>
            </Button>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-[#7A7A7A]">{listening ? 'Listening…' : t(lang,'voice_tap')}</div>
            <Button onClick={finishAndReport} disabled={generatingReport || messages.length<3}
              variant="outline" className="w-full sm:w-auto rounded-full border-2 border-[#1E3F33] text-[#1E3F33] font-semibold hover:bg-[#F4F1EA]"
              data-testid="generate-report-btn">
              {generatingReport ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <FileText className="h-4 w-4 mr-2"/>}
              {t(lang,'generate_report')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
